import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useChapterNavigation } from '../../hooks/useChapterNavigation';
import ProjectHero from './ProjectHero';
import ProjectMeta from './ProjectMeta';
import ProjectOverview from './ProjectOverview';
import ProjectGoal from './ProjectGoal';
import ProjectReflections from './ProjectReflections';
import ProjectChallenge from './ProjectChallenge';
import ProjectDesignProcess from './ProjectDesignProcess';
import ProjectProblem from './ProjectProblem';
import ProjectResults from './ProjectResults';
import ProjectFinalDesign from './ProjectFinalDesign';
import ProjectAchievements from './ProjectAchievements';
import ChapterNavigation from './ChapterNavigation';
import SectionHeading from '../shared/SectionHeading';
import { TEXT_STYLES } from '../../constants/styles';
import RelatedProjects from './RelatedProjects';

function ProjectDetail({ project }) {
  const location = useLocation();
  const { activeSection, scrollToSection } = useChapterNavigation(project);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!project) {
    return <div>Project not found</div>;
  }

  return (
    <div className="w-full overflow-x-hidden bg-white min-h-screen">
      <ProjectHero 
        title={project.title}
        heroImage={project.heroImage}
        heroDescription={project.heroDescription}
        headerBackground={project.headerBackground}
        logo={project.heroLogo || project.logo}
      />

      <section id="overview" className="py-12 md:py-16 px-6 md:px-12 scroll-mt-32">
        <div className="max-w-4xl mx-auto">
          {project.meta && (
            <ProjectMeta 
              role={project.meta.role}
              timeline={project.meta.timeline}
              tools={project.meta.tools}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-8 md:mb-16">
            {project.overview && (
              <ProjectOverview 
                title={project.overview.title}
                paragraphs={project.overview.paragraphs}
                liveUrl={project.liveUrl}
              />
            )}
            {project.goal && (
              <ProjectGoal 
                title={project.goal.title}
                description={project.goal.description}
              />
            )}
          </div>

          {project.introduction && (
            <div className="mb-8 md:mb-16">
              <div className="prose prose-lg">
                <h2 className={TEXT_STYLES.subheading + ' mb-2'}>
                  {project.introduction.title}
                </h2>
                <p className={TEXT_STYLES.body}>
                  {project.introduction.description}
                </p>
              </div>
            </div>
          )}

          {project.userResearch && (
            <div id="the-problem" className="max-w-3xl mx-auto mb-16 scroll-mt-32">
              <div className="prose prose-lg">
                <h2 className="text-2xl md:text-3xl font-playfair font-normal text-[#0D0D0D] mb-6">
                  {project.userResearch.title}
                </h2>
                <div className="text-base text-[#666666] font-normal leading-relaxed mb-8">
                  {project.userResearch.description.split('\n\n').map((paragraph, index) => (
                    <p key={index} className={index > 0 ? 'mt-6' : ''}>
                      {paragraph}
                    </p>
                  ))}
                </div>
                {project.userResearch.problemImages && project.userResearch.problemImages.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
                    {project.userResearch.problemImages.map((image, index) => (
                      <div key={index} className="w-full">
                        <img
                          src={image}
                          alt={`Problem ${index + 1}`}
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                    ))}
                  </div>
                )}
                {project.userResearch.quotes && project.userResearch.quotes.length > 0 && (
                  <div className="border-l-4 p-6 md:p-8 rounded-none mb-6" style={{ borderColor: project.headerBackground }}>
                    <div className="space-y-4">
                      {project.userResearch.quotes.map((quote, index) => (
                        <p key={index} className="text-lg md:text-xl font-semibold" style={{ color: project.headerBackground }}>
                          "{quote}"
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                {!project.userResearch.quotes && project.userResearch.keyIssue && (
                  <div className="border-l-4 p-6 md:p-8 rounded-none mb-6" style={{ borderColor: project.headerBackground }}>
                    <p className="text-lg md:text-xl font-semibold" style={{ color: project.headerBackground }}>
                      "{project.userResearch.keyIssue}"
                    </p>
                  </div>
                )}
                {project.userResearch.problemSection && (
                  <div className="mb-8">
                    <h2 className="text-2xl md:text-3xl font-playfair font-normal text-[#0D0D0D] mb-6">
                      {project.userResearch.problemSection.title}
                    </h2>
                    <p className="text-base text-[#666666] font-normal leading-relaxed">
                      {project.userResearch.problemSection.description}
                    </p>
                  </div>
                )}
                {project.userResearch.image && (
                  <div className="mt-6">
                    <img
                      src={project.userResearch.image}
                      alt="How Might We"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {project.achievements && (
            <ProjectAchievements
              title="Achievements"
              achievements={project.achievements}
            />
          )}

          {project.reflections && (
            <ProjectReflections
              title={project.reflections.title}
              paragraphs={project.reflections.paragraphs}
            />
          )}

          {project.challenge && (
            <ProjectChallenge 
              title={project.challenge.title}
              description={project.challenge.description}
              problemAreas={project.challenge.problemAreas}
              problemIds={project.problems ? project.problems.map(p => p.id) : []}
              headerBackground={project.headerBackground}
            />
          )}

          {project.problems && project.problems.map((problem, index) => (
            <ProjectProblem
              key={index}
              id={problem.id}
              title={problem.title}
              whatWasHappening={problem.whatWasHappening}
              keyFindings={problem.keyFindings}
              whyItMattered={problem.whyItMattered}
              designFocus={problem.designFocus}
              oldStructure={problem.oldStructure}
              newStructure={problem.newStructure}
              headerImage={problem.headerImage}
            />
          ))}

          {project.designProcess && (
            <ProjectDesignProcess
              title={project.designProcess.title}
              paragraphs={project.designProcess.paragraphs}
              image={project.designProcess.image}
            />
          )}

          {project.results && (
            <ProjectResults 
              title={project.results.title}
              description={project.results.description}
              hpImage={project.results.hpImage}
              finalDesignImages={project.results.finalDesignImages}
              finalDesignTitle={project.results.finalDesignTitle}
              finalDesignContent={project.results.finalDesignContent}
              finalDesignRows={project.results.finalDesignRows}
              finalDesignFooterImage={project.results.finalDesignFooterImage}
            />
          )}

          {project.visualTreatment && (
            <div id="visual-treatment" className="max-w-3xl mx-auto mt-16 scroll-mt-32">
              <div className="prose prose-lg">
                <SectionHeading>{project.visualTreatment.title}</SectionHeading>
                <div className={`${TEXT_STYLES.body} mt-6`}>
                  {project.visualTreatment.description.split('\n\n').map((paragraph, index) => {
                    // Check if this is a list item (starts with •)
                    if (paragraph.startsWith('•')) {
                      return (
                        <p key={index} className={index > 0 ? 'mt-4' : 'mt-6'}>
                          {paragraph}
                        </p>
                      );
                    }
                    // Regular paragraph
                    return (
                      <p key={index} className={index > 0 ? 'mt-6' : ''}>
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
                {project.visualTreatment.image && (
                  <div className="mt-8">
                    <img
                      src={project.visualTreatment.image}
                      alt="Brand Identity"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {project.finalDesign && (
            <ProjectFinalDesign
              title={project.finalDesign.title}
              description={project.finalDesign.description}
              images={project.finalDesign.images}
              content={project.finalDesign.content}
            />
          )}
        </div>
      </section>

      <ChapterNavigation 
        activeSection={activeSection}
        scrollToSection={scrollToSection}
        project={project}
      />

      <RelatedProjects currentProjectId={project.id} />
    </div>
  );
}

export default ProjectDetail;

